import { NEW_WEEKPICKER_FLAG } from '@/constants/experimentConstants';
import { useConfidenceFlag } from '@/hooks/useConfidenceFlag';

/**
 * The redesigned weekpicker's kill switch: resolving `new-weekpicker` to `{ enabled: false }`
 * takes the picker down and the legacy calendar body serves in its place. It is ANDed with the
 * redesign cohort flag at the call site, so the picker only ever ships to the new booking flow.
 *
 * An operational flag, not an experiment (`GLOBAL_OPERATIONAL_FLAGS` in server/index.js): the
 * server resolves it context-less for every HTML request and hydrates `state.flags`, so it is
 * read here with `shouldFetch: false` — the same shape GiftcardCheckout uses for
 * `physical-giftcard`. A client-side fetch would silently skip every visitor who has not
 * accepted analytics cookies, leaving the switch unable to take the picker down for them.
 *
 * Fail-open, unlike the cohort flag it is ANDed with: only a resolution Confidence actually
 * produced kills the picker, so a failed resolve (which the server deliberately does not cache)
 * leaves a shipped feature up rather than taking it down.
 */
export function useWeekpickerKillSwitch(): { isKilled: boolean } {
  const { flagValue } = useConfidenceFlag(NEW_WEEKPICKER_FLAG.name, NEW_WEEKPICKER_FLAG.defaultValue, false);

  return { isKilled: flagValue?.enabled === false };
}

export default useWeekpickerKillSwitch;
